import {_RequestEnvelope} from '../core/types';
import {
    FaqItem,
    FaqItemSummary,
    SiteContent,
    SiteContentSummary,
    SocialLink,
} from '../models/content';

// ---------------------------------------------------------------------------
// AddFaqItem
// ---------------------------------------------------------------------------

export interface AddFaqItemRequest {
    RealmId: string;
    Category: string;
    Title: string;
    Message: string;
    DisplayOrder: number | null;
}

export interface AddFaqItemResponse {
    FaqItem: FaqItem;
}

export function addFaqItem(params: AddFaqItemRequest): _RequestEnvelope<AddFaqItemResponse> {
    return {...params, _endpoint: 'admin/content/addFaqItem', _resultCodeMap: null};
}

// ---------------------------------------------------------------------------
// AddSiteContent
// ---------------------------------------------------------------------------

export interface AddSiteContentRequest {
    RealmId: string;
    Key: string;
    Title: string;
    Content: string;
}

export const AddSiteContentResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    KeyAlreadyExists: 'KeyAlreadyExists',
} as const;
export type AddSiteContentResult =
    typeof AddSiteContentResultCode[keyof typeof AddSiteContentResultCode];

export type AddSiteContentResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    Entry: SiteContent;
}
    | {
    Result: 'Unknown' | 'KeyAlreadyExists';
    ResultDescription: string;
    Entry: null;
};

const _addSiteContentMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'KeyAlreadyExists',
};

export function addSiteContent(
    params: AddSiteContentRequest
): _RequestEnvelope<AddSiteContentResponse> {
    return {...params, _endpoint: 'admin/content/addSiteContent', _resultCodeMap: _addSiteContentMap};
}

// ---------------------------------------------------------------------------
// GetFaqItem
// ---------------------------------------------------------------------------

export interface GetFaqItemRequest {
    RealmId: string;
    FaqId: string;
}

export const GetFaqItemResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NotFound: 'NotFound',
} as const;
export type GetFaqItemResult =
    typeof GetFaqItemResultCode[keyof typeof GetFaqItemResultCode];

export type GetFaqItemResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    Item: FaqItem;
}
    | {
    Result: 'Unknown' | 'NotFound';
    ResultDescription: string;
    Item: null;
};

const _getFaqItemMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NotFound',
};

export function getFaqItem(
    params: GetFaqItemRequest
): _RequestEnvelope<GetFaqItemResponse> {
    return {...params, _endpoint: 'admin/content/getFaqItem', _resultCodeMap: _getFaqItemMap};
}

// ---------------------------------------------------------------------------
// GetSiteContent
// ---------------------------------------------------------------------------

export interface GetSiteContentRequest {
    RealmId: string;
    Key: string;
}

export const GetSiteContentResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NotFound: 'NotFound',
} as const;
export type GetSiteContentResult =
    typeof GetSiteContentResultCode[keyof typeof GetSiteContentResultCode];

export type GetSiteContentResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    Entry: SiteContent;
}
    | {
    Result: 'Unknown' | 'NotFound';
    ResultDescription: string;
    Entry: null;
};

const _getSiteContentMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NotFound',
};

export function getSiteContent(
    params: GetSiteContentRequest
): _RequestEnvelope<GetSiteContentResponse> {
    return {...params, _endpoint: 'admin/content/getSiteContent', _resultCodeMap: _getSiteContentMap};
}

// ---------------------------------------------------------------------------
// GetSocialLinks
// ---------------------------------------------------------------------------

export interface GetSocialLinksRequest {
    RealmId: string;
}

export interface GetSocialLinksResponse {
    Links: SocialLink[];
}

export function getSocialLinks(params: GetSocialLinksRequest): _RequestEnvelope<GetSocialLinksResponse> {
    return {...params, _endpoint: 'admin/content/getSocialLinks', _resultCodeMap: null};
}

// ---------------------------------------------------------------------------
// ListFaqs
// ---------------------------------------------------------------------------

export interface ListFaqsRequest {
    RealmId: string;
    Category: string | null;
}

export interface ListFaqsResponse {
    Items: FaqItemSummary[];
}

export function listFaqs(params: ListFaqsRequest): _RequestEnvelope<ListFaqsResponse> {
    return {...params, _endpoint: 'admin/content/listFaqs', _resultCodeMap: null};
}

// ---------------------------------------------------------------------------
// ListSiteContent
// ---------------------------------------------------------------------------

export interface ListSiteContentRequest {
    RealmId: string;
}

export interface ListSiteContentResponse {
    Items: SiteContentSummary[];
}

export function listSiteContent(params: ListSiteContentRequest): _RequestEnvelope<ListSiteContentResponse> {
    return {...params, _endpoint: 'admin/content/listSiteContent', _resultCodeMap: null};
}

// ---------------------------------------------------------------------------
// SetSocialLinks
// ---------------------------------------------------------------------------

export interface SetSocialLinksRequest {
    RealmId: string;
    Links: SocialLink[];
}

export interface SetSocialLinksResponse {
    Links: SocialLink[];
}

export function setSocialLinks(params: SetSocialLinksRequest): _RequestEnvelope<SetSocialLinksResponse> {
    return {...params, _endpoint: 'admin/content/setSocialLinks', _resultCodeMap: null};
}

// ---------------------------------------------------------------------------
// UpdateFaqItem
// ---------------------------------------------------------------------------

export interface UpdateFaqItemRequest {
    RealmId: string;
    FaqId: string;
    Title: string;
    Message: string;
    Category: string;
    DisplayOrder: number | null;
}

export const UpdateFaqItemResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NotFound: 'NotFound',
} as const;
export type UpdateFaqItemResult =
    typeof UpdateFaqItemResultCode[keyof typeof UpdateFaqItemResultCode];

export type UpdateFaqItemResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    Item: FaqItem;
}
    | {
    Result: 'Unknown' | 'NotFound';
    ResultDescription: string;
    Item: null;
};

const _updateFaqItemMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NotFound',
};

export function updateFaqItem(
    params: UpdateFaqItemRequest
): _RequestEnvelope<UpdateFaqItemResponse> {
    return {...params, _endpoint: 'admin/content/updateFaqItem', _resultCodeMap: _updateFaqItemMap};
}

// ---------------------------------------------------------------------------
// UpdateSiteContent
// ---------------------------------------------------------------------------

export interface UpdateSiteContentRequest {
    RealmId: string;
    Key: string;
    Title: string;
    Content: string;
}

export const UpdateSiteContentResultCode = {
    Unknown: 'Unknown',
    Ok: 'Ok',
    NotFound: 'NotFound',
} as const;
export type UpdateSiteContentResult =
    typeof UpdateSiteContentResultCode[keyof typeof UpdateSiteContentResultCode];

export type UpdateSiteContentResponse =
    | {
    Result: 'Ok';
    ResultDescription: string;
    Entry: SiteContent;
}
    | {
    Result: 'Unknown' | 'NotFound';
    ResultDescription: string;
    Entry: null;
};

const _updateSiteContentMap: Record<number, string> = {
    0: 'Unknown',
    1: 'Ok',
    2: 'NotFound',
};

export function updateSiteContent(
    params: UpdateSiteContentRequest
): _RequestEnvelope<UpdateSiteContentResponse> {
    return {...params, _endpoint: 'admin/content/updateSiteContent', _resultCodeMap: _updateSiteContentMap};
}

// ---------------------------------------------------------------------------
// UploadSharedContent
// ---------------------------------------------------------------------------

export interface UploadSharedContentRequest {
    FilePath: string | null;
    ContentBase64: string;
    ContentType: string | null;
    UseExactPath: boolean;
}

export interface UploadSharedContentResponse {
    RelativeUrl: string;
    FullUrl: string | null;
}

export function uploadSharedContent(params: UploadSharedContentRequest): _RequestEnvelope<UploadSharedContentResponse> {
    return {...params, _endpoint: 'admin/content/uploadSharedContent', _resultCodeMap: null};
}
